/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Experiment,
  type ExperimentClient,
  type Variant,
} from '@amplitude/experiment-js-client';

import { getConfig } from '@/config';
import { AMPLITUDE_FEATURE_FLAGS } from '@/lib/amplitude-feature-flags';
import { useAuth } from '@/lib/auth';

import { clearAmplitudeExperimentSdkStorage } from './clear-amplitude-sdk-storage';
import { createExperimentUserFromSession } from './experiment-user';
import {
  getAmplitudeLocalFlagValues,
  shouldApplyAmplitudeFeatureFlagOverrides,
} from './url-and-storage';
import { variantToBoolean } from './variant-boolean';

const ALL_FLAG_KEYS = Object.values(AMPLITUDE_FEATURE_FLAGS) as string[];

/** Minimum interval enforced by Amplitude SDK for flag-config polling. */
const FLAG_CONFIG_POLLING_INTERVAL_MS = 60_000;

export type AmplitudeExperimentRefetchOptions = {
  /**
   * When true, do not toggle the context `loading` flag.
   * Use for in-place refresh (e.g. staff dialog) to avoid layout shift.
   */
  silent?: boolean;
  /**
   * When true, wipe Amplitude SDK localStorage (variants + flag configs) before
   * re-fetching. Use for manual reload in the debug dialog — not on initial auth sync.
   */
  bustSdkCache?: boolean;
};

export type AmplitudeExperimentContextValue = {
  client: ExperimentClient | null;
  loading: boolean;
  error: Error | null;
  /** Re-fetch flag configs and remote variants for the current user */
  refetch: (options?: AmplitudeExperimentRefetchOptions) => Promise<void>;
  /** Bumps when remote variants change so consumers re-evaluate */
  variantEpoch: number;
  /**
   * Bumps when localStorage Amplitude staff overrides change — call after mutating
   * overrides so {@link getBooleanValue} re-evaluates without a full page reload.
   */
  localOverrideEpoch: number;
  notifyLocalOverridesChanged: () => void;
  /** Remote evaluation only (ignores local staff overrides) */
  getRemoteBooleanValue: (
    flag: AMPLITUDE_FEATURE_FLAGS,
    defaultValue: boolean
  ) => boolean;
  getBooleanValue: (
    flag: AMPLITUDE_FEATURE_FLAGS,
    defaultValue: boolean
  ) => boolean;
};

export const AmplitudeExperimentContext =
  createContext<AmplitudeExperimentContextValue | null>(null);

export const AmplitudeExperimentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user, loading: authLoading } = useAuth();
  const deploymentKey = getConfig().amplitudeExperimentDeploymentKey;

  const clientRef = useRef<ExperimentClient | null>(null);
  const [loading, setLoading] = useState(Boolean(deploymentKey));
  const [error, setError] = useState<Error | null>(null);
  const [variantEpoch, setVariantEpoch] = useState(0);
  const [localOverrideEpoch, setLocalOverrideEpoch] = useState(0);

  const notifyLocalOverridesChanged = useCallback(() => {
    setLocalOverrideEpoch((n) => n + 1);
  }, []);

  const getClient = useCallback((): ExperimentClient | null => {
    if (!deploymentKey) {
      return null;
    }
    if (!clientRef.current) {
      clientRef.current = Experiment.initialize(deploymentKey, {
        fetchOnStart: false,
        pollOnStart: true,
        flagConfigPollingIntervalMillis: FLAG_CONFIG_POLLING_INTERVAL_MS,
        automaticExposureTracking: true,
      });
    }
    return clientRef.current;
  }, [deploymentKey]);

  const sync = useCallback(
    async (options?: AmplitudeExperimentRefetchOptions) => {
      const client = getClient();
      if (!client) {
        setLoading(false);
        return;
      }
      const silent = options?.silent === true;
      let setLoadingThisRun = false;
      if (!silent) {
        setLoading(true);
        setLoadingThisRun = true;
      }
      setError(null);
      try {
        const experimentUser = createExperimentUserFromSession(user);
        if (options?.bustSdkCache) {
          clearAmplitudeExperimentSdkStorage(client);
        }
        // stop() resets isRunning so start() re-downloads flag configs (doFlags).
        // Without this, subsequent refetches skip flag-config refresh because start() is a no-op.
        client.stop();
        await client.start(experimentUser);
        await client.fetch(experimentUser, { flagKeys: ALL_FLAG_KEYS });
        setVariantEpoch((n) => n + 1);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (setLoadingThisRun) {
          setLoading(false);
        }
      }
    },
    [getClient, user]
  );

  useEffect(() => {
    if (!deploymentKey) {
      setLoading(false);
      return;
    }
    if (authLoading) {
      return;
    }
    void sync();
  }, [
    deploymentKey,
    authLoading,
    user?.id,
    user?.email,
    user?.saas_tenant_id,
    user?.is_admin,
    sync,
  ]);

  useEffect(() => {
    return () => {
      clientRef.current?.stop();
    };
  }, []);

  const getRemoteBooleanValue = useCallback(
    (flag: AMPLITUDE_FEATURE_FLAGS, defaultValue: boolean): boolean => {
      const client = getClient();
      if (!client) {
        return defaultValue;
      }
      const fallback: Variant = {
        value: defaultValue ? 'on' : 'off',
      };
      const v = client.variant(flag, fallback);
      return variantToBoolean(v, defaultValue);
    },
    [getClient]
  );

  const getBooleanValue = useCallback(
    (flag: AMPLITUDE_FEATURE_FLAGS, defaultValue: boolean): boolean => {
      if (shouldApplyAmplitudeFeatureFlagOverrides()) {
        const local = getAmplitudeLocalFlagValues();
        if (Object.prototype.hasOwnProperty.call(local, flag)) {
          return Boolean(local[flag]);
        }
      }
      return getRemoteBooleanValue(flag, defaultValue);
    },
    // localOverrideEpoch: re-create callback when staff overrides change so context value updates
    [getRemoteBooleanValue, localOverrideEpoch]
  );

  const value = useMemo<AmplitudeExperimentContextValue>(
    () => ({
      client: getClient(),
      loading,
      error,
      refetch: sync,
      variantEpoch,
      localOverrideEpoch,
      notifyLocalOverridesChanged,
      getRemoteBooleanValue,
      getBooleanValue,
    }),
    [
      getClient,
      loading,
      error,
      sync,
      variantEpoch,
      localOverrideEpoch,
      notifyLocalOverridesChanged,
      getRemoteBooleanValue,
      getBooleanValue,
    ]
  );

  return (
    <AmplitudeExperimentContext.Provider value={value}>
      {children}
    </AmplitudeExperimentContext.Provider>
  );
};

export const useAmplitudeExperimentContext =
  (): AmplitudeExperimentContextValue => {
    const ctx = useContext(AmplitudeExperimentContext);
    if (!ctx) {
      throw new Error(
        'useAmplitudeExperimentContext must be used within AmplitudeExperimentProvider'
      );
    }
    return ctx;
  };
